/** Pure Sites inventory/inspect display mapping — no I/O. D4B. */

import type { StatusTone } from "./overview-ops";

export type SiteInventoryRow = {
  id: string;
  code: string;
  name: string;
  location: string | null;
  /** Present only when admin list (`listAllSites`) is available. */
  active?: boolean;
  codeLocked?: boolean;
  groupName: string | null;
  resellerLabel: string | null;
};

export type SiteStatusFilter = "all" | "active" | "inactive";
export type SiteSortKey = "name" | "code";

export function siteLifecycleLabelFa(active: boolean | undefined): string {
  if (active === undefined) return "فعال";
  return active ? "فعال" : "غیرفعال";
}

export function siteLifecycleTone(active: boolean | undefined): StatusTone {
  if (active === undefined) return "success";
  return active ? "success" : "neutral";
}

export function siteCodeLockLabelFa(codeLocked: boolean | undefined): string {
  if (codeLocked === undefined) return "—";
  return codeLocked ? "قفل‌شده" : "آزاد";
}

export function siteCodeLockTone(codeLocked: boolean | undefined): StatusTone {
  if (codeLocked === undefined) return "neutral";
  return codeLocked ? "info" : "neutral";
}

export function siteGroupLabelFa(groupName: string | null | undefined): string {
  return groupName?.trim() ? groupName : "بدون گروه";
}

export function siteResellerLabelFa(label: string | null | undefined): string {
  return label?.trim() ? label : "ندارد";
}

/** Muted inventory line: location · code lock · reseller */
export function siteInventoryTitleMeta(row: SiteInventoryRow): string {
  const parts: string[] = [];
  if (row.location?.trim()) parts.push(row.location.trim());
  if (row.codeLocked !== undefined) {
    parts.push(`کد ${siteCodeLockLabelFa(row.codeLocked)}`);
  }
  if (row.resellerLabel?.trim()) parts.push(row.resellerLabel.trim());
  return parts.join(" · ");
}

export function matchesSiteQuery(row: SiteInventoryRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    row.name,
    row.code,
    row.location ?? "",
    row.groupName ?? "",
    row.resellerLabel ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

export function matchesSiteStatus(row: SiteInventoryRow, status: SiteStatusFilter): boolean {
  if (status === "all") return true;
  if (row.active === undefined) return status === "active";
  return status === "active" ? row.active : !row.active;
}

export function filterAndSortSites(
  rows: SiteInventoryRow[],
  opts: { query: string; status: SiteStatusFilter; sort: SiteSortKey },
): SiteInventoryRow[] {
  const filtered = rows.filter(
    (row) => matchesSiteQuery(row, opts.query) && matchesSiteStatus(row, opts.status),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "code") {
      return a.code.localeCompare(b.code, "en", { sensitivity: "base" });
    }
    return a.name.localeCompare(b.name, "fa", { sensitivity: "base" });
  });
  return sorted;
}

export type RelatedSiteLink = {
  id: string;
  label: string;
  to: string;
};

/** Links to existing routes only — no invented query params on other pages. */
export function relatedSiteLinks(): RelatedSiteLink[] {
  return [
    { id: "fleet", label: "ناوگان و استقرار", to: "/fleet" },
    { id: "drift", label: "ناهماهنگی تنظیمات", to: "/fleet?tab=drift" },
    { id: "bundle", label: "Bundle", to: "/portal-bundles" },
    { id: "reseller", label: "پروفایل‌های نماینده", to: "/reseller-profiles" },
  ];
}
